import {
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FileSignature,
  Plus,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Button, ModalContentWrapper, Spinner } from '@/components/ui';
import { useDocuments } from '@/hooks/use-documents';
import {
  type SignerRole,
  type SubmissionStatus,
  useCancelSubmission,
  useRemindSubmitter,
  useSendDocumentForSigning,
  useSignatureSubmissions,
} from '@/hooks/use-e-signature';
import { cn } from '@/lib/cn';
import { fmtDate, fmtDateTime } from '@/lib/format';
import { toast } from '@/stores/toast-store';

interface Props {
  loanId: string;
}

const STATUS_CONFIG: Record<SubmissionStatus, { label: string; color: string; bgColor: string }> = {
  PENDING: { label: 'Pending', color: 'text-amber-700', bgColor: 'bg-amber-50' },
  PARTIALLY_SIGNED: { label: 'Partially signed', color: 'text-blue-700', bgColor: 'bg-blue-50' },
  COMPLETED: { label: 'Completed', color: 'text-green-700', bgColor: 'bg-green-50' },
  EXPIRED: { label: 'Expired', color: 'text-red-700', bgColor: 'bg-red-50' },
  CANCELLED: { label: 'Cancelled', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  DECLINED: { label: 'Declined', color: 'text-red-700', bgColor: 'bg-red-50' },
};

const SIGNER_STATUS_CONFIG = {
  PENDING: { icon: Clock, color: 'text-gray-500' },
  VIEWED: { icon: Clock, color: 'text-blue-600' },
  SIGNED: { icon: CheckCircle2, color: 'text-green-600' },
  DECLINED: { icon: XCircle, color: 'text-red-600' },
} as const;

const SIGNER_ROLES: Array<{ value: SignerRole; label: string }> = [
  { value: 'BORROWER', label: 'Borrower' },
  { value: 'GUARANTOR', label: 'Guarantor' },
  { value: 'WITNESS', label: 'Witness' },
  { value: 'DIRECTOR', label: 'Director' },
  { value: 'OTHER', label: 'Other' },
];

type SignerInput = { name: string; email: string; role: SignerRole };

const INITIAL_SIGNER: SignerInput = { name: '', email: '', role: 'BORROWER' };

export function SignatureTab({ loanId }: Props) {
  const { data: submissions = [], isLoading } = useSignatureSubmissions({ loanId });
  const { data: documents = [], isLoading: isLoadingDocuments } = useDocuments(loanId);
  const signableDocuments = documents.filter(document =>
    [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ].includes(document.mimeType),
  );
  const sendDocumentForSigning = useSendDocumentForSigning();
  const cancelSubmission = useCancelSubmission();
  const remindSubmitters = useRemindSubmitter();

  const [showModal, setShowModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    documentId: '',
    signers: [INITIAL_SIGNER],
    message: '',
    expiresAt: '',
  });

  function resetForm() {
    setFormData({
      documentId: '',
      signers: [INITIAL_SIGNER],
      message: '',
      expiresAt: '',
    });
  }

  async function handleCreate() {
    if (!formData.documentId) {
      toast.error('Select a document.');
      return;
    }

    const signers = formData.signers.filter(signer => signer.email.trim());
    if (signers.length === 0 || signers.some(signer => !signer.name.trim())) {
      toast.error('Enter a name and email address for every signer.');
      return;
    }

    try {
      await sendDocumentForSigning.mutateAsync({
        loanId,
        documentId: formData.documentId,
        signers,
        message: formData.message || undefined,
        expiresAt: formData.expiresAt || undefined,
      });
      toast.success('Signing request sent by email.');
      setShowModal(false);
      resetForm();
    } catch {
      // API error handling presents the server response to the user.
    }
  }

  async function handleReminder(submissionId: string) {
    try {
      await remindSubmitters.mutateAsync(submissionId);
      toast.success('Reminder sent to pending signers.');
    } catch {
      // API error handling presents the server response to the user.
    }
  }

  async function handleCancel(submissionId: string) {
    try {
      await cancelSubmission.mutateAsync(submissionId);
      toast.success('Signing request cancelled.');
    } catch {
      // API error handling presents the server response to the user.
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading signature requests...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">E-Signature Requests</h3>
        <Button
          variant="primary"
          onClick={() => setShowModal(true)}
          className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-sm"
        >
          <Plus className="w-4 h-4" />
          Request Signatures
        </Button>
      </div>

      {submissions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <FileSignature className="w-10 h-10 mb-3" />
          <p className="text-sm">No signature requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(submission => {
            const isExpanded = expandedId === submission.id;
            const signedCount = submission.signers.filter(
              signer => signer.status === 'SIGNED',
            ).length;
            const status = STATUS_CONFIG[submission.status];

            return (
              <div
                key={submission.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  type="button"
                  className="w-full p-4 bg-gray-50 text-left hover:bg-gray-100 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : submission.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {submission.template?.name || 'Signing request'}
                        </span>
                        <span
                          className={cn(
                            'text-xs px-2 py-1 rounded-full font-medium',
                            status.bgColor,
                            status.color,
                          )}
                        >
                          {status.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{fmtDate(submission.createdAt)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 text-xs text-gray-500">
                      <span>
                        {signedCount}/{submission.signers.length} signed
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="p-4 bg-white border-t border-gray-200 space-y-4">
                    {submission.message && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">Message</p>
                        <p className="text-sm text-gray-600">{submission.message}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-3">Signers</p>
                      <div className="space-y-2">
                        {submission.signers.map(signer => {
                          const signerStatus = SIGNER_STATUS_CONFIG[signer.status];
                          const StatusIcon = signerStatus.icon;
                          return (
                            <div
                              key={signer.id}
                              className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200"
                            >
                              <StatusIcon className={cn('w-5 h-5 shrink-0', signerStatus.color)} />
                              <div className="min-w-0 flex-1 text-sm">
                                <p className="font-medium text-gray-900">{signer.name}</p>
                                <p className="text-gray-500 truncate">{signer.email}</p>
                                {signer.role && (
                                  <p className="text-xs text-gray-500 mt-1">{signer.role}</p>
                                )}
                                {signer.signedAt && (
                                  <p className="text-xs text-green-600 mt-1">
                                    Signed on {fmtDateTime(signer.signedAt)}
                                  </p>
                                )}
                              </div>
                              <span className={cn('text-xs font-medium', signerStatus.color)}>
                                {signer.status}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {submission.expiresAt && (
                      <p className="text-xs text-gray-600">
                        Expires {fmtDate(submission.expiresAt)}
                      </p>
                    )}

                    <div className="flex justify-end gap-2 border-t border-gray-200 pt-3">
                      {submission.status === 'PENDING' && (
                        <Button
                          variant="secondary"
                          onClick={() => handleReminder(submission.id)}
                          disabled={remindSubmitters.isPending}
                          className="w-fit flex items-center gap-1.5"
                        >
                          <Bell className="w-4 h-4" />
                          Send reminder
                        </Button>
                      )}
                      {['PENDING', 'PARTIALLY_SIGNED'].includes(submission.status) && (
                        <Button
                          variant="secondary"
                          onClick={() => handleCancel(submission.id)}
                          disabled={cancelSubmission.isPending}
                          className="w-fit text-red-600 hover:text-red-700"
                        >
                          Cancel request
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <ModalContentWrapper
          open={showModal}
          onClose={() => {
            setShowModal(false);
            resetForm();
          }}
          title="Request Signatures"
        >
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Document</label>
              <select
                value={formData.documentId}
                onChange={event => setFormData({ ...formData, documentId: event.target.value })}
                disabled={isLoadingDocuments}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">
                  {isLoadingDocuments ? 'Loading documents...' : 'Select a document'}
                </option>
                {signableDocuments.map(document => (
                  <option key={document.id} value={document.id}>
                    {document.name}
                  </option>
                ))}
              </select>
              {!isLoadingDocuments && signableDocuments.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  Upload a PDF or DOCX document to this loan before sending a request.
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">Signers</label>
              <div className="space-y-3">
                {formData.signers.map((signer, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-md border border-gray-200"
                  >
                    <input
                      type="text"
                      value={signer.name}
                      onChange={event => {
                        const signers = [...formData.signers];
                        signers[index] = { ...signers[index], name: event.target.value };
                        setFormData({ ...formData, signers });
                      }}
                      placeholder="Full name"
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="email"
                      value={signer.email}
                      onChange={event => {
                        const signers = [...formData.signers];
                        signers[index] = { ...signers[index], email: event.target.value };
                        setFormData({ ...formData, signers });
                      }}
                      placeholder="Email address"
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <select
                      value={signer.role}
                      onChange={event => {
                        const signers = [...formData.signers];
                        signers[index] = {
                          ...signers[index],
                          role: event.target.value as SignerRole,
                        };
                        setFormData({ ...formData, signers });
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {SIGNER_ROLES.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <Button
                variant="secondary"
                onClick={() =>
                  setFormData({ ...formData, signers: [...formData.signers, INITIAL_SIGNER] })
                }
                className="w-fit mt-3 text-blue-600 hover:text-blue-700"
              >
                Add another signer
              </Button>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Message</label>
              <textarea
                value={formData.message}
                onChange={event => setFormData({ ...formData, message: event.target.value })}
                placeholder="Optional message to include in the email..."
                rows={3}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Expiry Date</label>
              <input
                type="date"
                value={formData.expiresAt}
                onChange={event => setFormData({ ...formData, expiresAt: event.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="w-fit"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreate}
                disabled={sendDocumentForSigning.isPending || !formData.documentId}
                className="w-fit"
              >
                {sendDocumentForSigning.isPending ? 'Sending...' : 'Send for Signing'}
              </Button>
            </div>
          </div>
        </ModalContentWrapper>
      )}
    </div>
  );
}
